import {
  setFilterDistrict,
  setFilterIsLeague,
  setFilterLeague,
  setFilterSchool,
  setFilterState,
} from 'app/imports/App.actions'
import { UISpinner } from 'features/UI'
import { useAppDispatch } from 'hooks'
import { ReactNode, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { WidgetTypes } from 'types'
import { getQueryParams } from 'utils'
import { setFilterSport, setFilterSportChangeable } from '../../../Filters/state/Filters.reducer'
import {
  useGetDistrictsQuery,
  useGetLeaguesQuery,
  useGetSchoolByIdsQuery,
  useGetSportsQuery,
  useGetStateByIdsQuery,
} from '../../state/Init.rtk'
import { selectWidgetType } from '../../state/Init.selectors'

export function InitData({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch()
  const widgetType = useSelector(selectWidgetType)
  const isStateWidget = widgetType === WidgetTypes.State
  const isStateLeagueWidget = widgetType === WidgetTypes.StateLeague
  const isLeagueWidget = widgetType === WidgetTypes.League
  const isCountyWidget = widgetType === WidgetTypes.District
  const isSchoolWidget = widgetType === WidgetTypes.School

  const { state, district, school, league, isLeague, sport } = getQueryParams()

  const stateIds = useMemo(() => {
    return state ? state.split(',').filter(Boolean) : []
  }, [state])

  const districtIds = useMemo(
    () => (district ? district.split(',').filter(Boolean) : []),
    [district],
  )

  const leagueIds = useMemo(() => (league ? league.split(',').filter(Boolean) : []), [league])

  const schoolIds = useMemo(() => (school ? school.split(',').filter(Boolean) : []), [school])

  const sportIds = useMemo(() => (sport ? sport.split(',').filter(Boolean) : []), [sport])

  const { data: stateData, isLoading: isStateLoading } = useGetStateByIdsQuery(stateIds, {
    skip: stateIds.length === 0,
  })

  const { data: districtData, isLoading: isCountiesLoading } = useGetDistrictsQuery(stateIds, {
    skip: districtIds.length === 0 || stateIds.length === 0,
  })

  const { data: leaguesData, isLoading: isLeaguesLoading } = useGetLeaguesQuery('', {
    skip: isLeague !== 'true',
  })

  const { data: sportsData, isLoading: isSportsLoading } = useGetSportsQuery(undefined, {
    skip: stateIds.length === 0,
  })

  const { data: schoolData, isLoading: isSchoolLoading } = useGetSchoolByIdsQuery(schoolIds, {
    skip: schoolIds.length === 0 && !isSchoolWidget,
  })

  const isLoading = useMemo(() => {
    if (isStateLeagueWidget || isLeagueWidget) {
      return isStateLoading && isLeaguesLoading && isSportsLoading
    }

    if (isStateWidget || isCountyWidget) {
      return isStateLoading && isCountiesLoading && isSportsLoading
    }

    if (isSchoolWidget) {
      return isStateLoading && isCountiesLoading && isSchoolLoading
    }

    return false
  }, [
    isCountiesLoading,
    isCountyWidget,
    isLeagueWidget,
    isLeaguesLoading,
    isSchoolLoading,
    isSchoolWidget,
    isSportsLoading,
    isStateLeagueWidget,
    isStateLoading,
    isStateWidget,
  ])

  useEffect(() => {
    if (stateData) {
      dispatch(setFilterState(stateData))
    }

    if (districtData && districtIds.length > 0) {
      const districtValues = districtData.filter((districtItem) =>
        districtIds.includes(districtItem.id),
      )
      dispatch(setFilterDistrict(districtValues))
    }

    if (schoolData && schoolIds.length > 0) {
      const schoolValues = schoolData.filter((schoolItem) => schoolIds.includes(schoolItem.id))
      dispatch(setFilterSchool(schoolValues))
    }

    if (leaguesData) {
      const leaguesValues = leaguesData.items.filter((leagueItem) =>
        leagueIds.includes(leagueItem.id),
      )
      dispatch(setFilterLeague(leaguesValues))
    }

    if (isLeague === 'true') {
      dispatch(setFilterIsLeague(true))
    }
  }, [
    dispatch,
    district,
    districtData,
    districtIds,
    isLeague,
    isSchoolWidget,
    league,
    leaguesData,
    leagueIds,
    schoolData,
    schoolIds,
    stateData,
  ])

  useEffect(() => {
    // if we have sport in query params
    if (sportsData && sportIds.length > 0) {
      const sportValues = sportsData.items.filter((sportItem) => sportIds.includes(sportItem.id))
      if (sportValues.length > 0) {
        dispatch(setFilterSportChangeable(false))
        dispatch(setFilterSport(sportValues))
      }
    }
  }, [dispatch, sportIds, sportsData])

  if (isLoading) {
    return <UISpinner />
  }

  return <>{children}</>
}
